// Utah State University: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - USU's Common Data Set 2025-2026 (Office of Data Analytics): C1
//    applicants, admits and enrollees for Fall 2025 (the residency table
//    gives totals only, so there are no residency rows), C8 test policy for
//    Fall 2027, C21/C22 (no early plans), G1/G5 costs for 2026-2027.
//  - usu.edu admissions: Apply (first-year requirements and the fall
//    deadlines table for the Logan campus) and Test Optional.
//  - College Scorecard API, school id 230728 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'USU Common Data Set 2025-2026',
        url: 'https://www.usu.edu/oda/files/common-data-sets/CDS2025-26.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'USU Admissions, Apply',
        url: 'https://www.usu.edu/admissions/apply/',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'USU Admissions, Test Optional',
        url: 'https://www.usu.edu/admissions/apply/test-optional.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?230728-Utah-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'utah-state-university',
    kbId: 'utah_state_university',
    name: 'Utah State University',
    shortName: 'Utah State',
    aliases: ['USU', 'Utah State'],
    location: 'Logan, UT',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (USU brand colors guide); decoration only.
    branding: {
        primary: '#0F2439',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/utah_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 21235,
        admitted: 19562,
        enrolled: 4594,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'USU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major. Many majors set a higher GPA or other requirements than the university\'s.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'USU has no early decision or early action plan; its March 1 date is a priority admission deadline.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final deadline (Logan)', value: 'August 1' },
        rows: [
            { plan: 'Priority deadline (Logan and Statewide)', deadline: 'March 1', decision: null, binding: false },
            { plan: 'Final deadline (Logan)', deadline: 'August 1', decision: null, binding: false },
            { plan: 'Final deadline (Statewide and USU Eastern)', deadline: 'September 1', decision: null, binding: false },
        ],
        note: 'The priority deadline is the last day to be admitted before priority registration; students admitted after the final deadline may be deferred to a later semester. The $50 application fee applies to nonresidents only. USU does not publish decision dates.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Admission to USU is test-optional, and ACT or SAT scores are still considered if you send them. A high school GPA of 2.8 to 4.0 means admission in good standing; below 2.8 you may still qualify through your GPA and test score together, and 2.0 to 2.79 is considered for earned admission. USU recommends scores for academic scholarships.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testOptional',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [8272, 25717] },
            { item: 'Required fees', amounts: [793, 793] },
            { item: 'Housing and food (on campus)', amounts: [9746, 9746] },
            { item: 'Books and supplies', amounts: [856, 856] },
            { item: 'Transportation', amounts: [2648, 2648] },
            { item: 'Other expenses', amounts: [1628, 1628] },
        ],
        totals: [23943, 41388],
        note: 'For a full-time undergraduate living on campus. Tuition varies by program for some students.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 12644,
            byIncome: [
                { income: '$0 to $30,000', amount: 12621 },
                { income: '$30,001 to $48,000', amount: 9813 },
                { income: '$48,001 to $75,000', amount: 10819 },
                { income: '$75,001 to $110,000', amount: 14451 },
                { income: 'Over $110,000', amount: 18605 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from USU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
